import styled from "styled-components";
import { Box } from "@lib/components";
import {
	MIN_TABLE_ROW_PX,
	TABLE_COL_WIDTH_NARROW_PX,
	TABLE_COL_WIDTH_WIDE_PX,
} from "./calendar.helpers";

/** Cloudtable-inspired page shell: queue + bento grid. */
export const CalendarShell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	position: relative;
	z-index: 0;
	padding: 0 16px 16px;
	@media (min-width: 1024px) {
		flex-direction: row;
		align-items: stretch;
		gap: 1.5rem;
		min-height: 620px;
	}
`;

export const CalendarPageHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin-bottom: 0;
	@media (min-width: 768px) {
		flex-direction: row;
		align-items: flex-end;
		justify-content: space-between;
		gap: 1rem;
	}
`;

export const CalendarTitle = styled.h2`
	margin: 0;
	font-size: 1.75rem;
	font-weight: 800;
	color: #0f172a;
	line-height: 1.15;
	letter-spacing: -0.02em;
`;

export const CalendarSubtitle = styled.p`
	margin: 0;
	font-size: 0.8125rem;
	font-weight: 600;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.12em;
`;

export const CalendarToolbar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
`;

/** Date-navigation pill — matches Figma node 20:11923 */
export const ToolbarPill = styled.div`
	display: inline-flex;
	align-items: center;
	background: #fff;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	overflow: hidden;
	height: 40px;
`;

export const ToolbarIconBtn = styled.button`
	border: none;
	background: transparent;
	padding: 0 10px;
	height: 100%;
	cursor: pointer;
	color: #414651;
	font-size: 0.875rem;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s ease;
	&:hover {
		background: #f9fafb;
	}
`;

export const ToolbarTodayBtn = styled.button`
	border: none;
	border-left: 1px solid #d5d7da;
	border-right: 1px solid #d5d7da;
	background: transparent;
	padding: 0 12px;
	height: 100%;
	min-width: 140px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.35rem;
	font-size: 14px;
	font-weight: 600;
	color: #414651;
	white-space: nowrap;
	&:hover {
		background: #f9fafb;
	}
	svg {
		color: #9da4ae;
		font-size: 0.8rem;
	}
`;

/** Turn-duration pill — matches Figma node 20:11924 */
export const TurnPill = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: #fff;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	padding: 0 12px;
	height: 40px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
`;

export const TurnPillLabel = styled.span`
	font-size: 14px;
	font-weight: 600;
	color: #414651;
	white-space: nowrap;
`;

export const TurnPillInput = styled.input`
	width: 3.5rem;
	border: none;
	background: transparent;
	font-size: 14px;
	font-weight: 500;
	color: #717680;
	text-align: center;
	padding: 0;
	&:focus {
		outline: none;
	}
`;

export const ToolbarDownloadBtn = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: 1px solid #d5d7da;
	background: #fff;
	color: #414651;
	border-radius: 8px;
	height: 40px;
	padding: 0 12px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	transition: background 0.15s ease, border-color 0.15s ease;
	&:hover {
		background: #f9fafb;
		border-color: #9da4ae;
	}
	svg {
		font-size: 0.875rem;
		color: inherit;
	}
`;

export const DatePickerBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1200;
	background: rgba(15, 23, 42, 0.28);
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
`;

export const DatePickerCard = styled.div`
	width: min(360px, calc(100vw - 2rem));
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 24px;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
	padding: 1rem;
`;

export const DatePickerHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin-bottom: 0.85rem;
`;

export const DatePickerMonthTitle = styled.div`
	font-size: 0.95rem;
	font-weight: 900;
	color: #0f172a;
	text-transform: uppercase;
	letter-spacing: 0.06em;
`;

export const DatePickerNavBtn = styled.button`
	width: 34px;
	height: 34px;
	border: 1px solid #e2e8f0;
	border-radius: 999px;
	background: #fff;
	color: #475569;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition:
		background 0.15s ease,
		border-color 0.15s ease;
	&:hover {
		background: #f8fafc;
		border-color: #cbd5e1;
	}
`;

export const DatePickerWeekdays = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 0.35rem;
	margin-bottom: 0.35rem;
`;

export const DatePickerWeekday = styled.div`
	text-align: center;
	font-size: 0.62rem;
	font-weight: 900;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.08em;
`;

export const DatePickerGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 0.35rem;
`;

export const DatePickerDayBtn = styled.button<{
	$selected: boolean;
	$today: boolean;
	$outside: boolean;
}>`
	aspect-ratio: 1;
	border: 1px solid ${(p) => (p.$selected ? "#fb2f2f" : p.$today ? "#fda29b" : "transparent")};
	border-radius: 14px;
	background: ${(p) => (p.$selected ? "#fb2f2f" : p.$today ? "#fff1f0" : "#fff")};
	color: ${(p) => (p.$selected ? "#fff" : p.$outside ? "#cbd5e1" : "#0f172a")};
	font-size: 0.82rem;
	font-weight: 800;
	cursor: pointer;
	transition:
		background 0.15s ease,
		border-color 0.15s ease,
		color 0.15s ease,
		transform 0.15s ease;
	&:hover {
		background: ${(p) => (p.$selected ? "#e02424" : "#f8fafc")};
		border-color: #fda29b;
		transform: translateY(-1px);
	}
`;

export const QueuePanel = styled(Box)`
	width: 100%;
	flex-shrink: 0;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
	background: #fff;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	@media (min-width: 1024px) {
		width: 300px;
		max-width: 100%;
	}
`;

/** "All Tables" filter section — top of queue panel with dashed separator */
export const QueueFilterHeader = styled.div`
	padding: 12px;
	border-bottom: 1px dashed #dfdfdf;
	background: #fff;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const QueueFilterSelect = styled.select`
	width: 100%;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	padding: 6px 28px 6px 12px;
	font-size: 12px;
	font-weight: 500;
	color: #181d27;
	background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 10px center;
	appearance: none;
	cursor: pointer;
	height: 32px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	&:focus { outline: none; border-color: #fb2f2f; }
`;

export const QueueTableSearchWrap = styled.label`
	display: flex;
	align-items: center;
	gap: 6px;
	background: #fff;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	padding: 0 10px;
	height: stretch;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	&:focus-within { border-color: #fb2f2f; }
`;

export const QueueTableSearchInput = styled.input`
	flex: 1;
	border: none;
	background: transparent;
	font-size: 14px;
	font-weight: 400;
	color: #181d27;
	padding: 0;
	&::placeholder { color: #9da4ae; }
	&:focus { outline: none; }
`;

/** Content body — title + card list */
export const QueueContentBody = styled.div`
	flex: 1;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	padding: 16px;
	gap: 12px;
`;

export const QueuePendingTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #4b4f5e;
	flex-shrink: 0;
`;

/** Kept for cancel-placement button use */
export const QueueHeader = styled.div`
	display: none;
`;

export const QueueList = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 0;
	min-height: 0;
`;

/** "Assign Seat" call-to-action button for expanded queue cards */
export const QueueAssignSeatBtn = styled.button`
	width: 100%;
	border: 2px solid rgba(255, 255, 255, 0.12);
	border-radius: 8px;
	padding: 7px 12px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	color: #fff;
	background: #fb2f2f;
	margin-top: 10px;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	transition: background 0.15s;
	&:hover { background: #e02424; }
`;

/** Cloudtable-style queue card (unallocated list). */
export const QueueCardRoot = styled.div<{
	$active: boolean;
	$unconfirmed: boolean;
}>`
	width: 100%;
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 10px;
	cursor: pointer;
	border: 2px solid
		${(p) =>
			p.$active
				? "#fda29b"
				: p.$unconfirmed
					? "#fda29b"
					: "#e2e8f0"};
	background: #fff;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
	${(p) =>
		p.$active
			? `background: #fef3f2;`
			: ""}
	&:hover {
		border-color: ${(p) => (p.$unconfirmed ? "#fb2f2f" : "#c8cdd5")};
		box-shadow: 0 2px 8px rgba(10, 13, 18, 0.08);
	}
`;

export const QueueCardTop = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 8px;
`;

export const QueueGuest = styled.span<{ $unconfirmed: boolean }>`
	font-size: 14px;
	font-weight: 500;
	color: #414651;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
	display: block;
	line-height: 20px;
`;

export const QueueMetaRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 2px;
`;

export const QueueTime = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: #535862;
	line-height: 18px;
	white-space: nowrap;
`;

export const QueueParty = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: #6b7270;
	white-space: nowrap;
`;

export const QueueBadge = styled.span<{ $variant: "assign" | "ready" }>`
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;
	border-radius: 9999px;
	padding: 0 8px 0 6px;
	height: 18px;
	display: inline-flex;
	align-items: center;
	${(p) =>
		p.$variant === "assign"
			? "background: #fef3f2; color: #b42318; border: 1px solid #fecdca;"
			: "background: #ecfdf3; color: #067647; border: 1px solid #abefc6;"}
`;

export const QueuePlaceHint = styled.span`
	font-size: 9px;
	font-weight: 800;
	color: #fb2f2f;
	text-transform: uppercase;
	letter-spacing: 0.12em;
`;

export const QueueFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 0.5rem;
`;

export const QueueConfirmActions = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 4px;
	margin-top: 16px;
`;

export const QueueConfirmBtn = styled.button<{ $primary?: boolean }>`
	border: ${(p) => (p.$primary ? "2px solid rgba(255,255,255,0.12)" : "1px solid #d5d7da")};
	border-radius: 8px;
	height: 32px;
	padding: 0 12px;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	cursor: pointer;
	color: ${(p) => (p.$primary ? "#fff" : "#414651")};
	background: ${(p) => (p.$primary ? "#fb2f2f" : "#fff")};
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	transition: background 0.15s, filter 0.15s;
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
	overflow: hidden;
	&:hover {
		filter: brightness(0.95);
	}
`;

export const CalendarFilterBar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.65rem;
	margin-bottom: 1rem;
`;

export const CalendarFilterField = styled.div<{ $wide?: boolean }>`
	display: flex;
	align-items: center;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
	height: 44px;
	padding: 0 0.85rem;
	width: ${(p) => (p.$wide ? "200px" : "168px")};
	max-width: 100%;
	flex: 0 0 auto;
	input,
	select {
		width: 100%;
		border: none;
		background: transparent;
		height: 100%;
		font-size: 0.8125rem;
		font-weight: 700;
		color: #0f172a;
		outline: none;
		min-width: 0;
	}
	select {
		cursor: pointer;
	}
`;

export const GridWrap = styled(Box)<{ $placing?: boolean }>`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	min-height: clamp(640px, calc(100vh - 220px), 840px);
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
	background: #fff;
	position: relative;
	z-index: 0;
	isolation: isolate;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
	${(p) =>
		p.$placing
			? `
		box-shadow: 0 0 0 3px rgba(251, 47, 47, 0.14);
		border-color: #fda29b !important;
	`
			: ""}
`;

export const ScrollArea = styled.div`
	overflow: auto;
	flex: 1;
	min-width: 0;
	min-height: 0;
	position: relative;
	background: #fff;
	-webkit-overflow-scrolling: touch;
	cursor: grab;
	&:active {
		cursor: grabbing;
	}
`;

export const StickyHeader = styled.div`
	display: flex;
	position: sticky;
	top: 0;
	z-index: 5;
	background: rgba(255, 255, 255, 0.96);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border-bottom: 1px solid #e2e8f0;
	box-shadow: 0 8px 20px -18px rgba(15, 23, 42, 0.5);
`;

export const CornerCell = styled.div`
	width: ${TABLE_COL_WIDTH_NARROW_PX}px;
	flex-shrink: 0;
	padding: 10px 12px;
	text-align: left;
	border-right: 1px solid #e2e8f0;
	background: #fff;
	font-size: 12px;
	font-weight: 600;
	color: #535862;
	position: sticky;
	left: 0;
	z-index: 13;
	@media (min-width: 768px) {
		width: ${TABLE_COL_WIDTH_WIDE_PX}px;
	}
`;

export const HourTrack = styled.div`
	display: flex;
	flex-shrink: 0;
`;

export const HourCell = styled.div<{ $w: number }>`
	width: ${(p) => p.$w}px;
	flex-shrink: 0;
	padding: 10px 0.4rem;
	border-left: 1px solid #e2e8f0;
	font-size: 12px;
	font-weight: 500;
	color: #535862;
	white-space: nowrap;
	text-align: center;
`;

export const GridRow = styled.div`
	display: flex;
	border-bottom: 1px solid #e2e8f0;
	transition: opacity 0.2s ease, filter 0.2s ease;
`;

export const RowLabel = styled.div`
	width: ${TABLE_COL_WIDTH_NARROW_PX}px;
	flex-shrink: 0;
	padding: 10px 12px;
	border-right: 1px solid #e2e8f0;
	background: #fff;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	position: sticky;
	left: 0;
	z-index: 13;
	box-shadow: 4px 0 8px -6px rgba(15, 23, 42, 0.1);
	@media (min-width: 768px) {
		width: ${TABLE_COL_WIDTH_WIDE_PX}px;
	}
`;

export const RowLabelTitle = styled.span`
	font-size: 13px;
	font-weight: 600;
	color: #181d27;
	line-height: 1.2;
`;

export const RowLabelMeta = styled.span`
	font-size: 11px;
	font-weight: 400;
	color: #9da4ae;
	margin-top: 2px;
`;

export const TimelineTrack = styled.div<{ $w: number; $slot: number; $minor: number }>`
	position: relative;
	min-height: ${MIN_TABLE_ROW_PX}px;
	width: ${(p) => p.$w}px;
	flex-shrink: 0;
	background-color: #fff;
	background-image: linear-gradient(
			to right,
			rgba(226, 232, 240, 0.62) 1px,
			transparent 1px
		),
		linear-gradient(to right, rgba(148, 163, 184, 0.35) 1px, transparent 1px);
	background-size: ${(p) => p.$minor}px 100%, ${(p) => p.$slot}px 100%;
`;

export const NowLine = styled.div<{ $left: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(p) => p.$left}px;
	width: 2px;
	background: #e53935;
	z-index: 4;
	pointer-events: none;
	opacity: 0.85;
	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: -4px;
		width: 10px;
		height: 10px;
		border-radius: 50%;
		background: #e53935;
	}
`;

export const BookingBlockOuter = styled.div<{
	$left: number;
	$width: number;
	$top: number;
	$laneHeight: number;
	$z: number;
	$pe?: string;
}>`
	position: absolute;
	left: ${(p) => p.$left}px;
	width: ${(p) => Math.max(p.$width, 48)}px;
	top: ${(p) => p.$top}px;
	height: ${(p) => p.$laneHeight - 4}px;
	min-height: 40px;
	z-index: ${(p) => p.$z};
	overflow: visible;
	pointer-events: ${(p) => p.$pe || "auto"};
`;

export const BookingBlockBtn = styled.button<{
	$status: "active" | "done" | "cancelled";
	$ring?: boolean;
	$dragging?: boolean;
	$joinSecondary?: boolean;
}>`
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 40px;
	border-radius: 6px;
	padding: 6px 8px;
	text-align: left;
	cursor: ${(p) => (p.$dragging ? "grabbing" : "grab")};
	border: 1px solid ${(p) =>
		p.$status === "done"
			? "#abefc6"
			: p.$status === "cancelled"
				? "#cbd5e1"
				: "#fb2f2f"};
	background: ${(p) =>
		p.$status === "done"
			? "#ecfdf3"
			: p.$status === "cancelled"
				? "#f8fafc"
				: "#fff3f3"};
	color: ${(p) =>
		p.$status === "done"
			? "#067647"
			: p.$status === "cancelled"
				? "#94a3b8"
				: "#fb2f2f"};
	overflow: hidden;
	transition: transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease;
	${(p) =>
		p.$joinSecondary
			? `
		opacity: 0.72;
		border-style: dashed;
		cursor: pointer;
	`
			: ""}
	${(p) =>
		p.$ring
			? p.$status === "done"
				? `box-shadow: 0 0 0 3px rgba(6,118,71,0.15); transform: scale(1.02); z-index: 1;`
				: `box-shadow: 0 0 0 3px rgba(251, 47, 47, 0.2); transform: scale(1.02); z-index: 1;`
			: ""}
	${(p) =>
		p.$dragging
			? `opacity: 0.45; transform: scale(0.98); cursor: grabbing;`
			: ""}
	&:hover {
		transform: scale(1.02);
		box-shadow: ${(p) =>
			p.$status === "done"
				? "0 2px 8px rgba(6,118,71,0.1)"
				: p.$status === "cancelled"
					? "0 2px 8px rgba(0,0,0,0.04)"
					: "0 2px 8px rgba(251, 47, 47, 0.12)"};
	}
`;

export const PartyBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 3px;
	font-size: 12px;
	font-weight: 400;
	color: inherit;
`;

export const QuickActionsBar = styled.div`
	position: absolute;
	top: -46px;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	align-items: center;
	gap: 0;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 999px;
	padding: 4px;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
	z-index: 30;
	white-space: nowrap;
`;

export const StatusMenuWrap = styled.div`
	position: relative;
`;

export const StatusMenuList = styled.div`
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 148px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
	padding: 4px;
	z-index: 8;
`;

export const StatusMenuItem = styled.button<{ $danger?: boolean }>`
	display: block;
	width: 100%;
	border: none;
	background: transparent;
	text-align: left;
	border-radius: 10px;
	padding: 8px 10px;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${(p) => (p.$danger ? "#be123c" : "#334155")};
	cursor: pointer;
	&:hover {
		background: ${(p) => (p.$danger ? "#fff1f2" : "#f8fafc")};
	}
`;

export const QuickActionPrimary = styled.button`
	background: #0f172a;
	color: #fff;
	border: none;
	border-radius: 999px;
	padding: 8px 14px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	margin-right: 2px;
	&:hover {
		background: #000;
	}
`;

export const QuickActionGhost = styled.button<{ $active?: boolean }>`
	background: ${(p) => (p.$active ? "#f1f5f9" : "transparent")};
	color: ${(p) => (p.$active ? "#0f172a" : "#64748b")};
	border: none;
	border-radius: 999px;
	padding: 8px 12px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

export const NotesPopover = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
	width: 260px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 16px;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
	padding: 12px;
	z-index: 25;
`;

export const PlacementSlot = styled.button<{
	$valid: boolean;
	$requested?: boolean;
	$suggested?: boolean;
	$hot?: boolean;
}>`
	position: absolute;
	top: 0;
	bottom: 0;
	border: none;
	padding: 0;
	margin: 0;
	z-index: ${(p) => (p.$requested || p.$suggested || p.$hot ? 20 : 15)};
	cursor: ${(p) => (p.$valid ? "pointer" : "default")};
	background: ${(p) => {
		if (p.$hot) return "rgba(16, 185, 129, 0.42)";
		if (p.$requested) return "rgba(16, 185, 129, 0.22)";
		if (p.$suggested) return "rgba(251, 47, 47, 0.14)";
		return p.$valid ? "rgba(251, 47, 47, 0.06)" : "transparent";
	}};
	box-shadow: ${(p) => {
		if (p.$hot) return "inset 0 0 0 2px #059669";
		if (p.$requested) return "inset 0 0 0 2px #10b981";
		if (p.$suggested) return "inset 0 0 0 2px #fb2f2f";
		return "none";
	}};
	pointer-events: ${(p) => (p.$valid ? "auto" : "none")};
	animation: ${(p) => (p.$suggested && !p.$hot ? "cwPulsePlaced 1.8s ease-in-out infinite" : "none")};
	&:hover {
		background: ${(p) => {
			if (!p.$valid) return "transparent";
			if (p.$hot) return "rgba(16, 185, 129, 0.42)";
			return "rgba(16, 185, 129, 0.26)";
		}};
		box-shadow: ${(p) => (p.$valid ? "inset 0 0 0 2px #059669" : "none")};
	}
	@keyframes cwPulsePlaced {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.72;
		}
	}
`;

/** Empty timeline slot → open “Add booking” pre-filled for this table/time. */
export const CreateTimelineSlot = styled.button<{ $valid: boolean }>`
	position: absolute;
	top: 0;
	bottom: 0;
	border: none;
	padding: 0;
	margin: 0;
	z-index: 1;
	cursor: ${(p) => (p.$valid ? "pointer" : "default")};
	background: transparent;
	box-shadow: none;
	pointer-events: ${(p) => (p.$valid ? "auto" : "none")};
	&:hover {
		background: transparent;
	}
`;

export const LinkCustomerPopover = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
	width: 280px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 16px;
	box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
	padding: 12px;
	z-index: 26;
`;

export const TableBlockStrip = styled.button<{ $left: number; $width: number }>`
	position: absolute;
	top: 4px;
	bottom: 4px;
	left: ${(p) => p.$left}px;
	width: ${(p) => Math.max(p.$width, 24)}px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 6px;
	border: 1px dashed #94a3b8;
	border-radius: 6px;
	background: repeating-linear-gradient(
		-45deg,
		rgba(100, 116, 139, 0.22),
		rgba(100, 116, 139, 0.22) 4px,
		rgba(241, 245, 249, 0.92) 4px,
		rgba(241, 245, 249, 0.92) 8px
	);
	color: #475569;
	font-size: 10px;
	font-weight: 700;
	line-height: 1.2;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	cursor: pointer;
	z-index: 8;
	&:hover {
		border-color: #64748b;
		box-shadow: inset 0 0 0 1px rgba(100, 116, 139, 0.25);
	}
`;

export const ClosedOverlayStrip = styled.div<{ $left: number; $width: number }>`
	position: absolute;
	top: 0;
	bottom: 0;
	left: ${(p) => p.$left}px;
	width: ${(p) => p.$width}px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: repeating-linear-gradient(
		-45deg,
		rgba(148, 163, 184, 0.2),
		rgba(148, 163, 184, 0.2) 5px,
		rgba(248, 250, 252, 0.78) 5px,
		rgba(248, 250, 252, 0.78) 10px
	);
	pointer-events: none;
	z-index: 1;
	overflow: hidden;
	&::after {
		content: "Closed";
		color: #94a3b8;
		font-size: 0.625rem;
		font-weight: 900;
		letter-spacing: 0.14em;
		line-height: 1;
		text-transform: uppercase;
		white-space: nowrap;
		transform: rotate(-2deg);
	}
`;

export const QuickActionDocket = styled.button`
	background: #fb2f2f;
	color: #fff;
	border: none;
	border-radius: 999px;
	padding: 8px 14px;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	cursor: pointer;
	margin-right: 2px;
	&:hover {
		background: #e02424;
	}
`;

export const DocketBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 200;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	background: rgba(15, 23, 42, 0.72);
	backdrop-filter: blur(6px);
	@media print {
		background: transparent;
		padding: 0;
	}
`;

export const DocketCard = styled.div`
	background: #fff;
	border-radius: 40px;
	box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
	max-width: 420px;
	width: 100%;
	padding: 2.25rem 2.5rem;
	text-align: center;
	@media print {
		box-shadow: none;
		border-radius: 0;
		max-width: none;
		padding: 1.5rem;
	}
`;

export const DocketEyebrow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.5rem;
`;

export const DocketEyebrowText = styled.span`
	font-size: 0.75rem;
	font-weight: 800;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.14em;
`;

export const DocketCloseBtn = styled.button`
	border: none;
	background: transparent;
	color: #cbd5e1;
	font-size: 1.25rem;
	line-height: 1;
	cursor: pointer;
	padding: 0.25rem;
	border-radius: 8px;
	&:hover {
		color: #0f172a;
		background: #f1f5f9;
	}
`;

export const DocketTableBand = styled.div`
	padding: 1.25rem 0 1.5rem;
	margin-bottom: 0.5rem;
	border-bottom: 2px dashed #e2e8f0;
	@media print {
		border-color: #cbd5e1;
	}
`;

export const DocketTableTitle = styled.h3`
	margin: 0;
	font-size: 2.25rem;
	font-weight: 900;
	color: #0f172a;
	letter-spacing: -0.03em;
	line-height: 1.1;
`;

export const DocketVenue = styled.p`
	margin: 0.35rem 0 0;
	font-size: 0.8125rem;
	font-weight: 700;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.1em;
`;

export const DocketGuestBlock = styled.div`
	padding: 1.5rem 0 1rem;
	text-align: center;
`;

export const DocketGuestLabel = styled.p`
	margin: 0 0 0.35rem;
	font-size: 0.8125rem;
	font-weight: 700;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.1em;
`;

export const DocketGuestName = styled.h4`
	margin: 0;
	font-size: 1.5rem;
	font-weight: 900;
	color: #0f172a;
	letter-spacing: -0.02em;
`;

export const DocketParty = styled.p`
	margin: 0.35rem 0 0;
	font-size: 1rem;
	font-weight: 600;
	color: #334155;
`;

export const DocketHintBox = styled.div`
	margin-top: 1rem;
	padding: 1.25rem;
	background: #f8fafc;
	border-radius: 1.75rem;
	border: 2px solid #f1f5f9;
	@media print {
		background: transparent;
		border-color: #cbd5e1;
	}
`;

export const DocketHintText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	font-weight: 700;
	color: #64748b;
	line-height: 1.5;
`;

export const DocketActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.65rem;
	margin-top: 1.5rem;
	@media print {
		display: none;
	}
`;

export const QueueLegendFooter = styled.div`
	padding: 0;
	background: transparent;
	border-top: none;
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	flex-shrink: 0;
`;

/** Figma node 59:2723 — empty queue state */
export const QueueEmptyState = styled.div`
	padding: 32px 0px 16px;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	width: 100%;
`;

export const QueueEmptyIcon = styled.div`
	width: 48px;
	height: 48px;
	border-radius: 10px;
	background: #fff;
	border: 1px solid #d5d7da;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05),
		inset 0 0 0 1px rgba(10, 13, 18, 0.18),
		inset 0 -2px 0 0 rgba(10, 13, 18, 0.05);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
	color: #535862;
	flex-shrink: 0;
`;

export const QueueEmptyTitle = styled.p`
	margin: 0;
	font-size: 16px;
	font-weight: 600;
	color: #181d27;
	line-height: 1.5;
`;

export const QueueEmptySubtext = styled.p`
	margin: 0;
	font-size: 14px;
	font-weight: 400;
	color: #535862;
	line-height: 1.43;
`;

export const LegendRow = styled.p`
	margin: 0;
	font-size: 12px;
	font-weight: 500;
	color: #535862;
	display: flex;
	align-items: center;
	gap: 10px;
`;

export const LegendSwatch = styled.span<{ $color: string }>`
	display: inline-block;
	width: 16px;
	height: 16px;
	border-radius: 4px;
	background: ${(p) => p.$color};
	flex-shrink: 0;
`;

/** Time-change confirmation (cloudtable-style), CW chrome. */
export const TimeAdjustBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 190;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	background: rgba(15, 23, 42, 0.72);
	backdrop-filter: blur(6px);
	@media print {
		display: none;
	}
`;

/** @deprecated use ResModalCard instead */
export const TimeAdjustCard = styled.div``;
/** @deprecated */
export const TimeAdjustIcon = styled.div``;

/* ─── Shared reservations modal (nodes 70:2844, 70:3325) ─── */

export const ResModalCard = styled.div`
	background: #fefefe;
	border-radius: 8px;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
	max-width: 440px;
	width: 100%;
	overflow: visible;
	padding-bottom: 24px;
	position: relative;
`;

export const ResModalHeader = styled.div`
	background: #fff;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	flex-shrink: 0;
`;

export const ResModalFeaturedIcon = styled.div`
	width: 48px;
	height: 48px;
	flex-shrink: 0;
	border-radius: 10px;
	border: 1px solid #d5d7da;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
	color: #535862;
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05),
		inset 0 0 0 1px rgba(10, 13, 18, 0.18),
		inset 0 -2px 0 rgba(10, 13, 18, 0.05);
`;

export const ResModalTitleCol = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
	min-width: 0;
`;

export const ResModalTitle = styled.p`
	font-size: 14px;
	font-weight: 500;
	color: #0c202a;
	margin: 0;
	line-height: normal;
`;

export const ResModalDesc = styled.p`
	font-size: 12px;
	font-weight: 400;
	color: #4b4f5e;
	margin: 0;
	line-height: 1.4;
`;

export const ResModalNumberBadge = styled.span`
	flex-shrink: 0;
	background: #fef3f2;
	border: 1px solid #fecdca;
	border-radius: 6px;
	padding: 2px 8px;
	font-size: 14px;
	font-weight: 500;
	color: #b42318;
	line-height: 20px;
	white-space: nowrap;
`;

export const ResModalCloseBtn = styled.button`
	position: absolute;
	top: -8px;
	right: -12px;
	width: 28px;
	height: 28px;
	border-radius: 999px;
	border: 1px solid #dee2e6;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 16px;
	color: #6b7280;
	line-height: 1;
	&:hover { background: #f1f5f9; color: #0f172a; }
`;

export const ResModalBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 0 12px;
`;

export const ResModalActionBtn = styled.button<{ $primary?: boolean; $full?: boolean }>`
	height: 32px;
	border-radius: 8px;
	padding: 0 12px;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	white-space: nowrap;
	transition: filter 0.15s;
	width: ${(p) => (p.$full ? "100%" : "auto")};
	border: ${(p) =>
		p.$primary ? "2px solid rgba(255,255,255,0.12)" : "1px solid #d5d7da"};
	background: ${(p) => (p.$primary ? "#fb2f2f" : "#fff")};
	color: ${(p) => (p.$primary ? "#fff" : "#414651")};
	box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
	&:hover { filter: brightness(0.95); }
`;

export const ResModalButtonRow = styled.div`
	display: flex;
	gap: 12px;
	> * { flex: 1; }
`;

export const ResModalInfoRow = styled.div`
	background: #fff;
	border-bottom: 1px solid #e2e8f0;
	padding: 16px;
`;

export const ResModalInfoBlock = styled.div`
	background: #fff;
	border-radius: 12px;
	padding: 0 16px 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

export const ResModalInfoTitle = styled.p`
	font-size: 14px;
	font-weight: 500;
	color: #414651;
	margin: 0;
	line-height: 20px;
`;

export const ResModalMetaRow = styled.div`
	display: flex;
	gap: 12px;
`;

export const ResModalMetaLabel = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: #535862;
	line-height: 18px;
`;

export const ResModalMetaValue = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: #6b7270;
	line-height: 18px;
`;

export const BlockTitle = styled.p<{ $strike?: boolean }>`
	font-size: 12px;
	font-weight: 600;
	margin: 0;
	line-height: 18px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: inherit;
	${(p) =>
		p.$strike ? `text-decoration: line-through; opacity: 0.6;` : ""}
`;

export const BlockTableLabel = styled.p`
	font-size: 10px;
	font-weight: 700;
	margin: 0;
	line-height: 14px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: inherit;
	opacity: 0.85;
`;

export const BlockMeta = styled.div`
	font-size: 12px;
	font-weight: 500;
	margin: 2px 0 0;
	color: inherit;
	display: flex;
	align-items: center;
	gap: 12px;
	white-space: nowrap;
	overflow: hidden;
`;

export const BlockMetaItem = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	svg {
		font-size: 12px;
		flex-shrink: 0;
	}
`;
